import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField,
  IconButton, InputAdornment, CircularProgress, Snackbar, Alert,
  LinearProgress, Grid, Avatar, Divider, Chip,
} from '@mui/material';
import {
  Visibility, VisibilityOff, Lock, Person,
  Shield, CheckCircle,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import { useAuth } from '../context/AuthContext';

// ── Password strength scorer ──────────────────────────────────────────────────
const getStrength = (pw) => {
  if (!pw) return { score: 0, label: '', color: '#e2e8f0' };
  let s = 0;
  if (pw.length >= 8)           s++;
  if (pw.length >= 12)          s++;
  if (/[A-Z]/.test(pw))         s++;
  if (/[0-9]/.test(pw))         s++;
  if (/[^A-Za-z0-9]/.test(pw))  s++;
  return [
    { score: 0, label: '',            color: '#e2e8f0' },
    { score: 1, label: 'Very Weak',   color: '#ef4444' },
    { score: 2, label: 'Weak',        color: '#f97316' },
    { score: 3, label: 'Fair',        color: '#f59e0b' },
    { score: 4, label: 'Strong',      color: '#22c55e' },
    { score: 5, label: 'Very Strong', color: '#16a34a' },
  ][s];
};

const ROLE_COLOR = {
  superadmin: 'error',
  admin:      'primary',
  manager:    'warning',
  support:    'default',
};

export default function Settings() {
  const { user } = useAuth();

  const [form, setForm] = useState({
    old_password: '', new_password: '', confirm_new_password: '',
  });
  const [show, setShow]     = useState({ old: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [snack, setSnack]   = useState({ open: false, msg: '', sev: 'success' });

  const strength    = getStrength(form.new_password);
  const strengthPct = (strength.score / 5) * 100;
  const passwordsMatch = form.confirm_new_password
    ? form.new_password === form.confirm_new_password
    : true;

  const set  = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const flip = (k) => () => setShow(s => ({ ...s, [k]: !s[k] }));

  const ToggleBtn = ({ k }) => (
    <InputAdornment position="end">
      <IconButton onClick={flip(k)} edge="end" size="small">
        {show[k] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.old_password || !form.new_password || !form.confirm_new_password) {
      setSnack({ open: true, msg: 'All fields are required.', sev: 'warning' }); return;
    }
    if (form.new_password !== form.confirm_new_password) {
      setSnack({ open: true, msg: 'New passwords do not match.', sev: 'error' }); return;
    }
    if (form.new_password.length < 8) {
      setSnack({ open: true, msg: 'Password must be at least 8 characters.', sev: 'warning' }); return;
    }
    if (strength.score < 2) {
      setSnack({ open: true, msg: 'Please choose a stronger password.', sev: 'warning' }); return;
    }
    setLoading(true);
    const res = await InvokeApi(Config.base_url + apiList.changePassword, {
      old_password: form.old_password,
      new_password: form.new_password,
    });
    setLoading(false);
    if (res?.code === '200' || res?.status === 'success') {
      setSnack({ open: true, msg: 'Password changed successfully.', sev: 'success' });
      setForm({ old_password: '', new_password: '', confirm_new_password: '' });
    } else {
      setSnack({ open: true, msg: res?.message || 'Failed to change password.', sev: 'error' });
    }
  };

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800} color="#1a237e">Settings</Typography>
        <Typography variant="body2" color="text.secondary">Manage your account and security</Typography>
      </Box>

      <Grid container spacing={3}>
        {/* ── Left column ─────────────────────────────────────────────────── */}
        <Grid item xs={12} md={7}>

          {/* Profile card */}
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box sx={{ bgcolor: '#1a237e18', borderRadius: 1.5, p: 1, display: 'flex' }}>
                  <Person sx={{ color: '#1a237e', fontSize: 20 }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={700} color="#1a237e">Profile</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2 }}>
                <Avatar
                  sx={{
                    width: 64, height: 64,
                    background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                    fontSize: 26, fontWeight: 800,
                  }}
                >
                  {(user?.name || 'A')[0].toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={800} color="#1a237e">
                    {user?.name || 'Admin User'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={user?.role || 'admin'}
                      color={ROLE_COLOR[user?.role?.toLowerCase()] || 'default'}
                      size="small"
                      sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                    />
                    <Chip
                      icon={<CheckCircle sx={{ fontSize: '14px !important' }} />}
                      label="Active"
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {[
                  ['Admin ID', user?.adminId || '—'],
                  ['Role',     user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : '—'],
                ].map(([label, value]) => (
                  <Grid item xs={6} key={label}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" fontWeight={600}>{value}</Typography>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Change password card */}
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box sx={{ bgcolor: '#1a237e18', borderRadius: 1.5, p: 1, display: 'flex' }}>
                  <Lock sx={{ color: '#1a237e', fontSize: 20 }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={700} color="#1a237e">Change Password</Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  fullWidth size="small" label="Current Password" sx={{ mb: 2 }}
                  type={show.old ? 'text' : 'password'}
                  value={form.old_password}
                  onChange={set('old_password')}
                  InputProps={{ endAdornment: <ToggleBtn k="old" /> }}
                />

                <TextField
                  fullWidth size="small" label="New Password" sx={{ mb: 1 }}
                  type={show.new ? 'text' : 'password'}
                  value={form.new_password}
                  onChange={set('new_password')}
                  InputProps={{ endAdornment: <ToggleBtn k="new" /> }}
                />

                {/* Strength meter */}
                {form.new_password && (
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress
                      variant="determinate" value={strengthPct}
                      sx={{ height: 6, borderRadius: 1, bgcolor: '#e2e8f0',
                        '& .MuiLinearProgress-bar': { bgcolor: strength.color, borderRadius: 1 } }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: strength.color, fontWeight: 700 }}>
                        {strength.label}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        {[
                          ['8+',  form.new_password.length >= 8],
                          ['A-Z', /[A-Z]/.test(form.new_password)],
                          ['0-9', /[0-9]/.test(form.new_password)],
                          ['!@#', /[^A-Za-z0-9]/.test(form.new_password)],
                        ].map(([label, met]) => (
                          <Typography key={label} variant="caption"
                            sx={{ color: met ? '#22c55e' : '#94a3b8', fontWeight: 600 }}>
                            {met ? '✓' : '○'} {label}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}

                <TextField
                  fullWidth size="small" label="Confirm New Password" sx={{ mb: 3 }}
                  type={show.confirm ? 'text' : 'password'}
                  value={form.confirm_new_password}
                  onChange={set('confirm_new_password')}
                  error={!passwordsMatch}
                  helperText={!passwordsMatch ? 'Passwords do not match' : ''}
                  InputProps={{ endAdornment: <ToggleBtn k="confirm" /> }}
                />

                <Button
                  type="submit" variant="contained" fullWidth
                  disabled={loading || !passwordsMatch}
                  sx={{ py: 1.3, fontWeight: 700, background: 'linear-gradient(135deg,#1a237e,#1565c0)' }}
                >
                  {loading
                    ? <CircularProgress size={20} sx={{ color: '#fff' }} />
                    : 'Update Password'
                  }
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* ── Right column ────────────────────────────────────────────────── */}
        <Grid item xs={12} md={5}>

          {/* Security tips */}
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ bgcolor: '#16a34a18', borderRadius: 1.5, p: 1, display: 'flex' }}>
                  <Shield sx={{ color: '#16a34a', fontSize: 20 }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={700} color="#1a237e">
                  Password Requirements
                </Typography>
              </Box>
              {[
                { label: 'At least 8 characters long',                     met: form.new_password.length >= 8 },
                { label: 'One uppercase letter (A–Z)',                      met: /[A-Z]/.test(form.new_password) },
                { label: 'One number (0–9)',                                met: /[0-9]/.test(form.new_password) },
                { label: 'One special character (!@#$%^&*)',                met: /[^A-Za-z0-9]/.test(form.new_password) },
                { label: '12+ characters for maximum security',             met: form.new_password.length >= 12 },
              ].map(({ label, met }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.2 }}>
                  <Box sx={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: form.new_password
                      ? met ? '#dcfce7' : '#fee2e2'
                      : '#f1f5f9',
                  }}>
                    <Typography variant="caption" fontWeight={800}
                      sx={{ color: form.new_password ? (met ? '#16a34a' : '#dc2626') : '#94a3b8', lineHeight: 1 }}>
                      {form.new_password ? (met ? '✓' : '✕') : '○'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>

          {/* Session info */}
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', bgcolor: '#f8f9ff', border: '1px solid #e8eaf6' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle2" fontWeight={700} color="#1a237e" sx={{ mb: 1.5 }}>
                Security Tips
              </Typography>
              {[
                'Never share your password with anyone, including team members.',
                'Use a unique password not used on other sites.',
                'Change your password regularly, especially after team changes.',
                'Log out from shared or public devices after use.',
              ].map((tip, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.2, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1a237e', mt: 0.8, flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary">{tip}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          sx={{ fontWeight: 600 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
